import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { RotateCcw, VideoOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

type VideoPlayerProps = React.ComponentProps<'video'> & {
  /** Classes applied to the inner <video> element (e.g. object-cover). */
  videoClassName?: string
  /**
   * When true, the component does not set `src` until the first play click.
   * This prevents eager video loading while keeping one-click playback.
   */
  deferSrcUntilPlay?: boolean
}

/**
 * Drop-in replacement for `<video>` that eliminates the browser's
 * native loading spinner.
 *
 * For videos with `controls`:
 *   Before first play → `controls` is OFF, a custom poster overlay
 *   with a play button is shown instead of native UI. No native spinner
 *   can ever appear because the shadow DOM controls don't exist yet.
 *   After first play → native `controls` are enabled and the overlay
 *   hides. The shimmer replaces the spinner during the loading gap.
 *
 * For videos without `controls` (e.g. hover-to-play gallery):
 *   The video is invisible until the first frame is ready. The
 *   wrapper's `bg-slate-100` shows through as a placeholder.
 *
 * A failed source load (deleted object → 404, transient proxy error → 502)
 * shows an error overlay with a retry button instead of a silently broken
 * player; retry re-requests the same URL via `video.load()`.
 */
export const VideoPlayer = forwardRef<HTMLVideoElement, VideoPlayerProps>(
  function VideoPlayer(
    {
      poster,
      src,
      className,
      style,
      videoClassName,
      preload,
      controls,
      deferSrcUntilPlay,
      onLoadedData,
      onPlaying,
      onError,
      ...props
    },
    ref,
  ) {
    const { t } = useTranslation()
    const innerRef = useRef<HTMLVideoElement>(null)
    useImperativeHandle(ref, () => innerRef.current!)

    const hasPoster = !!poster
    const shouldDeferSource =
      !!deferSrcUntilPlay && typeof src === 'string' && src.length > 0
    const effectivePreload = preload ?? (hasPoster ? 'none' : 'metadata')
    const [ready, setReady] = useState(hasPoster)
    const [started, setStarted] = useState(false)
    const [loading, setLoading] = useState(false)
    const [failed, setFailed] = useState(false)
    const [posterFailed, setPosterFailed] = useState(false)

    const attachSourceAndPlay = () => {
      if (!innerRef.current) return
      const video = innerRef.current
      setLoading(true)
      if (shouldDeferSource && video.dataset.srcAttached !== '1' && typeof src === 'string' && src.length > 0) {
        video.src = src
        video.dataset.srcAttached = '1'
      }
      video.play().catch(() => setLoading(false))
    }

    const handleRetry = () => {
      if (!innerRef.current) return
      setFailed(false)
      // load() re-requests the current src — a transient 502 succeeds on retry.
      innerRef.current.load()
      attachSourceAndPlay()
    }

    return (
      <div className={cn('relative overflow-hidden bg-slate-100', className)} style={style}>
        <video
          ref={innerRef}
          src={shouldDeferSource ? undefined : src}
          poster={poster}
          preload={effectivePreload}
          controls={controls ? started : undefined}
          className={cn('w-full h-full', videoClassName, !ready && 'invisible')}
          onLoadedData={(e) => {
            setReady(true)
            onLoadedData?.(e)
          }}
          onPlaying={(e) => {
            setStarted(true)
            setLoading(false)
            onPlaying?.(e)
          }}
          onError={(e) => {
            // Fires only for a real source failure; without an attached src
            // (deferred mode before first click) no error event occurs.
            setFailed(true)
            setLoading(false)
            onError?.(e)
          }}
          {...props}
        />

        {/* Custom overlay — only for videos with controls, before first play */}
        {controls && !started && !failed && (
          <div
            className="absolute inset-0 cursor-pointer"
            onClick={attachSourceAndPlay}
          >
            {/* Poster image rendered as <img> so no native spinner */}
            {poster && !posterFailed && (
              <img
                src={poster}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                onError={() => setPosterFailed(true)}
              />
            )}

            {loading ? (
              /* Shimmer while buffering after play click */
              <div className="absolute inset-0 bg-slate-200/60">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-shimmer" />
              </div>
            ) : (
              /* Play button */
              <div className="absolute inset-0 flex items-center justify-center bg-black/5 hover:bg-black/15 transition-colors">
                <div className="w-14 h-14 rounded-full bg-black/50 flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-7 h-7 text-white ml-0.5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error overlay — failed source load, retry re-requests the URL */}
        {failed && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-100 text-slate-500">
            <VideoOff className="w-8 h-8 max-w-[25%] max-h-[25%]" />
            {controls ? (
              <>
                <p className="text-sm px-2 text-center">{t('videoPlayer.loadFailed')}</p>
                <button
                  type="button"
                  onClick={handleRetry}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  {t('common.retry')}
                </button>
              </>
            ) : (
              /* Compact tiles (hover galleries): icon only, click to retry */
              <button
                type="button"
                onClick={handleRetry}
                className="absolute inset-0"
                aria-label={t('videoPlayer.reload')}
              />
            )}
          </div>
        )}
      </div>
    )
  },
)
